<template>
  <div
    class="modal fade"
    id="editCaseModal"
    data-backdrop="static"
    tabindex="-1"
    role="dialog"
    aria-labelledby="editCaseModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content bg-dashboard-dark-1" v-if="show">
        <div class="modal-header shadow-sm border-outline-dark-bottom">
          <h6
            class="modal-title font-weight-bold text-white"
            id="editCaseModalLabel"
          >
            <template v-if="show !== 'add'">
              <small class="d-block line-height-1 text-white">
                Case Number
              </small>
              {{ currentCase.caseNumber }}
            </template>
            <template v-else>
              Add new {{ isTender ? "Sebut Harga" : "case" }}
            </template>
          </h6>
          <button
            type="button"
            class="close text-white"
            aria-label="Close"
            @click="closeModal()"
            data-placement="bottom"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
            title="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body bg-dashboard-dark-1">
          <div class="row m-0">
            <div class="col">
              <div
                class="bg-dark-input form-group text-center p-2 rounded"
                v-if="!isTender"
              >
                <label class="small text-light">Select case type</label>
                <div class="btn-group btn-group-sm w-100" role="group">
                  <button
                    type="button"
                    class="btn"
                    :class="
                      !models.isNoe && !models.isNod
                        ? 'bg-dark-ds text-light border-light'
                        : 'btn-secondary'
                    "
                    @click="changeType('normal')"
                    data-placement="bottom"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                    title="Click to set case type as normal"
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="
                      models.isNod
                        ? 'bg-dark-ds text-light border-light'
                        : 'btn-secondary'
                    "
                    @click="changeType('nod')"
                    data-placement="bottom"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                    title="Click to set case type as NOD"
                  >
                    NOD
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="
                      models.isNoe
                        ? 'bg-dark-ds text-light border-light'
                        : 'btn-secondary'
                    "
                    @click="changeType('noe')"
                    data-placement="bottom"
                    @mouseover="helper.showTooltip"
                    @mouseleave="helper.hideTooltip"
                    title="Click to set case type as NOE"
                  >
                    NOE
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="row m-0">
            <div class="col">
              <div class="form-group">
                <label class="small text-light" for="latitude">Latitude</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="latitude"
                  v-model="models.lat"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Insert latitude"
                />
              </div>
            </div>
            <div class="col">
              <div class="form-group">
                <label class="small text-light" for="longitude"
                  >Longitude</label
                >
                <input
                  type="number"
                  step="any"
                  min="0"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="longitude"
                  v-model="models.lng"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Insert longitude"
                />
              </div>
            </div>
          </div>
          <div class="row m-0">
            <div class="col">
              <div class="form-group">
                <label class="small text-light" for="section">
                  Section<!-- <sup class="text-danger">*</sup> -->
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="section"
                  v-model="models.section"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Insert section"
                />
              </div>
            </div>
            <div class="col">
              <div class="form-group">
                <label class="small text-light" for="timestamp">
                  Date &amp; Time<sup class="text-danger">*</sup>
                </label>
                <p
                  class="form-control form-control-default form-control-sm d-flex justify-content-between align-items-center clickable bg-dark-input text-white"
                  :class="{ 'is-invalid': dateTimeError }"
                  @click="showDateSelector = true"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select date and time"
                >
                  <span>{{ formatDate(models.timestamp) }}</span>
                  <font-awesome-icon :icon="['far', 'calendar-alt']" />
                </p>
                <small class="text-danger" v-if="dateTimeError">
                  Please select a date &amp; time
                </small>
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col">
              <div class="form-group">
                <label class="small text-light" for="reportType">
                  Work Scope<!-- <sup class="text-danger">*</sup> -->
                </label>
                <select
                  id="reportType"
                  class="custom-select custom-select-sm bg-dark-input text-white"
                  v-model="models.reportType"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select work scope"
                >
                  <option :value="null" disabled>-- Select work scope</option>
                  <option
                    v-for="scope in workScopes"
                    :key="`scope-${scope}`"
                    :value="scope"
                  >
                    {{ scope }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col" v-if="!isTender">
              <div class="form-group">
                <label class="small text-light"> Category</label>
                <select
                  class="custom-select custom-select-sm bg-dark-input text-white"
                  v-model="models.category"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select category"
                >
                  <option :value="null" disabled>-- Select category</option>
                  <option
                    v-for="category in categories"
                    :key="category"
                    :value="category"
                  >
                    {{ category.split("_").join(" ") }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light">
                    District<sup class="text-danger">*</sup>
                  </label>
                </div>
                <select
                  class="custom-select custom-select-sm bg-dark-input text-white"
                  :class="{ 'is-invalid': districtError }"
                  v-model="models.district"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select district"
                >
                  <option :value="null" disabled>-- Select district</option>
                  <option
                    v-for="district in districts"
                    :key="district._id"
                    :value="district._id"
                  >
                    {{ district.name }}
                  </option>
                </select>
                <small class="text-danger" v-if="districtError">
                  Please select a district
                </small>
              </div>
            </div>
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light"> DUN </label>
                </div>
                <select
                  class="custom-select custom-select-sm bg-dark-input text-white"
                  v-model="models.dun"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select DUN"
                >
                  <option :value="null" disabled>-- Select DUN</option>
                  <option v-for="dun in duns" :key="dun._id" :value="dun._id">
                    {{ dun.kod }} {{ dun.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light">
                    Road
                    <!-- <sup class="text-danger">*</sup> -->
                  </label>
                </div>
                <div class="dropdown rounded w-100">
                  <button
                    class="btn btn-sm border btn-block dropdown-toggle position-relative d-flex justify-content-between align-items-center px-3 bg-dark-input text-white border-outline-dark"
                    style="font-size: 0.875rem; color: #495057"
                    type="button"
                    id="dropdownRoadButton"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <span v-if="roads.find((road) => road._id == models.road)">
                      {{ roads.find((road) => road._id == models.road).name }}
                    </span>
                    <span v-else class="text-muted">-- Select road</span>
                  </button>
                  <div
                    class="dropdown-menu py-0 w-100 shadow-sm text-white p-0"
                    aria-labelledby="dropdownRoadButton"
                    style="height: 200px; overflow: hidden; font-size: 0.875rem"
                  >
                    <input
                      class="form-control form-control-sm bg-dashboard-dark-2 border-outline-dark-bottom text-white"
                      placeholder="Search"
                      v-model="searchModel"
                      data-placement="top"
                      @mouseover="helper.showTooltip"
                      @mouseleave="helper.hideTooltip"
                      title="Select road name"
                    />
                    <div style="height: calc(100% - 43px); overflow-y: auto">
                      <button
                        v-for="road in roadList"
                        :key="road._id"
                        class="border-bottom dropdown-item d-flex justify-content-between align-items-center px-3 py-2 bg-dashboard-dark-1 border-outline-dark text-white"
                        :class="{
                          'bg-subtle': models.road && models.road == road._id,
                        }"
                        style="color: #495057"
                        @click="models.road = road._id"
                      >
                        <span class="fs-2">
                          {{ road.name }}
                          <font-awesome-icon
                            :icon="['fas', 'check']"
                            class="text-primary ml-2 small"
                            v-if="models.road && models.road == road._id"
                          />
                        </span>
                        <span class="small text-muted text-capitalize ml-3"
                          >({{ road.type }})</span
                        >
                      </button>
                      <div
                        class="dropdown-item py-2 small text-center text-muted font-italic bg-dashboard-dark-1 px-3 py-2 fs-8"
                      >
                        <span v-if="roadList.length > 0">End of list</span>
                        <span v-else>No road found</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light"
                    >Caption / Identified Defect</label
                  >
                </div>
                <textarea
                  v-model="models.caption"
                  rows="10"
                  class="form-control form-control-default form-control-sm bg-dark-input text-light"
                  style="resize: none"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Leave a caption / identified Defect here"
                />
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light">Remarks</label>
                </div>
                <textarea
                  v-model="models.remarks"
                  rows="5"
                  class="form-control form-control-default form-control-sm bg-dark-input text-light"
                  style="resize: none"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Leave a remarks here"
                />
              </div>
            </div>
          </div>
          <div class="row mx-0" v-if="!isTender">
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light"> Contractor </label>
                </div>
                <select
                  class="custom-select custom-select-sm bg-dark-input text-light"
                  v-model="models.contractor"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select contractor"
                >
                  <option :value="null" disabled>-- Select contractor</option>
                  <option
                    v-for="contractor in contractors"
                    :key="contractor._id"
                    :value="contractor._id"
                  >
                    {{ contractor.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col" v-if="!isR02">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light">Mix Type</label>
                </div>
                <select
                  class="custom-select custom-select-sm bg-dark-input text-light"
                  v-model="models.mix_type"
                  data-placement="top"
                  @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip"
                  title="Select mix type"
                >
                  <option :value="null" disabled>-- Select mix type</option>
                  <option v-for="type in mix_types" :key="type" :value="type">
                    {{ helper.mixTypeLabel(type) }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="row mx-0">
            <div class="col" v-if="!isR02">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light"
                    >Width (<span class="text-monospace">m</span>)</label
                  >
                </div>
                <input
                  type="number"
                  step="any"
                  min="0"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="width"
                  v-model="models.width"
                />
              </div>
            </div>
            <div class="col">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light pre"
                    >Length (<span class="text-monospace">m</span>)</label
                  >
                </div>
                <input
                  type="number"
                  step="any"
                  min="0"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="height"
                  v-model="models.height"
                />
              </div>
            </div>
            <div class="col" v-if="!isR02">
              <div class="form-group">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-light"
                    >Premix Quantity (<span class="text-monospace"
                      >m<sup>2</sup></span
                    >)</label
                  >
                </div>
                <!-- Derived (width x height) — not user input; backend recomputes on save -->
                <input
                  type="text"
                  class="form-control form-control-default form-control-sm bg-dark-input text-white"
                  id="premix_quantity"
                  :value="derivedPremix"
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer shadow border-outline-dark-top">
          <div
            class="mr-auto bg-dark rounded px-3 py-2 user-select-none"
            :class="models.isNotify ? 'text-primary' : 'text-muted'"
          >
            <div class="custom-control custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="setNofifySwitch"
                v-model="models.isNotify"
              />
              <label class="custom-control-label" for="setNofifySwitch">
                Set as Notify
              </label>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-link btn-sm"
            @click="closeModal()"
            data-placement="bottom"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
            title="Click to cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            @click="saveChanges()"
            :disabled="isProcessing"
            data-placement="bottom"
            @mouseover="helper.showTooltip"
            @mouseleave="helper.hideTooltip"
            title="Click to save"
          >
            <div
              class="spinner-border spinner-border-sm text-light"
              v-if="isProcessing"
            ></div>
            <span v-else> Save </span>
          </button>
        </div>
      </div>
    </div>
    <date-time-selector
      v-if="showDateSelector"
      :date="show !== 'add' ? models.timestamp : new Date()"
      @close="dateSelected"
    />
  </div>
</template>

<script>
import $ from "jquery";
import API from "@/api";
import Helper from "@/helper";
import Swal from "sweetalert2";
import DateTimeSelector from "@/components/DateTimeSelector";

export default {
  name: "edit-case-modal",
  props: [
    "show",
    "currentCase",
    "districts",
    "roads",
    "contractors",
    "isTender",
  ],
  components: {
    DateTimeSelector,
  },
  data() {
    return {
      helper: Helper,
      isProcessing: false,
      showDateSelector: false,
      districtError: false,
      dateTimeError: false,
      searchModel: "",
      categories: Helper.workCategories(),
      mix_types: ["hotmix", "coldmix", "heavy_patching"],
      duns: [],
      models: {
        lat: null,
        lng: null,
        section: null,
        road: null,
        district: null,
        dun: null,
        caption: null,
        timestamp: null,
        reportType: null,
        category: null,
        remarks: null,
        contractor: null,
        width: null,
        height: null,
        premix_quantity: null,
        mix_type: null,
        isNoe: false,
        isNod: false,
        isNotify: false,
        user: null,
      },
      emptyModels: {
        lat: null,
        lng: null,
        section: null,
        road: null,
        district: null,
        dun: null,
        caption: null,
        timestamp: null,
        reportType: null,
        category: null,
        remarks: null,
        contractor: null,
        width: null,
        height: null,
        premix_quantity: null,
        mix_type: null,
        isNoe: false,
        isNod: false,
        isNotify: false,
        user: null,
      },
    };
  },
  watch: {
    show: {
      async handler() {
        if (this.show) {
          $("#editCaseModal").modal("show");

          this.models = JSON.parse(JSON.stringify(this.emptyModels));

          if (this.show !== "add") {
            this.models.lat = this.currentCase.lat;
            this.models.lng = this.currentCase.lng;
            this.models.section = this.currentCase.section;
            this.models.caption = this.currentCase.caption;
            this.models.remarks = this.currentCase.remarks;
            this.models.timestamp = this.currentCase.timestamp;
            this.models.reportType = this.currentCase.reportType;
            this.models.category = this.currentCase.category;
            this.models.isNoe = this.currentCase.isNoe;
            this.models.isNod = this.currentCase.isNod;
            this.models.isNotify = this.currentCase.isNotify;
            this.models.width = this.currentCase.width;
            this.models.height = this.currentCase.height;
            this.models.premix_quantity = this.currentCase.premix_quantity;
            this.models.mix_type = this.currentCase.mix_type;
            this.models.dun = this.currentCase.dun
              ? this.currentCase.dun._id
              : null;
            this.models.district = this.currentCase.district
              ? this.currentCase.district._id
              : null;
            this.models.road = this.currentCase.road
              ? this.currentCase.road._id
              : null;
            this.models.contractor = this.currentCase.contractor
              ? this.currentCase.contractor._id
              : null;
            this.models.user = this.currentCase.user?.id || null;
          } else {
            const _user = localStorage.getItem("user");
            const userJson = JSON.parse(_user || "{}");

            if (userJson.contractor) {
              this.models.contractor = userJson.contractor._id;
            } else {
              this.models.contractor = null;
            }

            this.models.user = userJson?.id;
          }
        } else {
          $("#editCaseModal").modal("hide");
        }
      },
      immediate: true,
    },
    "models.district": {
      handler() {
        this.districtError = false;

        const selectedRoad = this.roads
          .filter((road) => road.daerah == this.models.district)
          .find((road) => road._id == this.models.road);

        if (!selectedRoad) {
          this.models.road = null;
        }
      },
      deep: true,
      immediate: true,
    },
    "models.road": {
      handler() {
        this.searchModel = "";
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    isR02() {
      return this.models.reportType == "R02";
    },
    // Premix quantity is derived (width x height), never typed — mirrors the
    // backend lifecycle derivation so the user sees what will be stored.
    derivedPremix() {
      if (this.isR02) return "";

      const w = parseFloat(this.models.width);
      const h = parseFloat(this.models.height);

      if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return "";

      return String(Math.round(w * h * 100) / 100);
    },
    workScopes() {
      return this.isTender
        ? ["Tender"]
        : [
            "KKE",
            "R01",
            "R02",
            "R03",
            "R04",
            "R05",
            "R06",
            "R07",
            "R08",
            "R09",
          ];
    },
    roadList() {
      let list = JSON.parse(
        JSON.stringify(
          this.roads.filter((road) => road.daerah == this.models.district)
        )
      );

      if (this.models.road) {
        const selectedRoad = list.find((l) => l._id == this.models.road);

        if (selectedRoad) {
          list = list.filter((l) => l._id != selectedRoad._id);

          list.unshift(selectedRoad);
        }
      }

      if (this.searchModel) {
        list = list.filter((l) =>
          l.name.toLowerCase().includes(this.searchModel.toLowerCase())
        );
      }

      return list;
    },
  },
  created() {
    this.getDuns();
  },
  methods: {
    async getDuns() {
      try {
        const { data } = await API.get("duns?_limit=-1&_sort=kod");

        this.duns = data;
      } catch (err) {
        console.error(err);
      }
    },
    changeType(type) {
      if (type == "nod") {
        this.models.isNod = true;
        this.models.isNoe = false;
      } else if (type == "noe") {
        this.models.isNod = false;
        this.models.isNoe = true;
      } else {
        this.models.isNod = false;
        this.models.isNoe = false;
      }
    },
    dateSelected(e) {
      if (e) {
        this.models.timestamp = e;
        this.dateTimeError = false;
      }

      this.showDateSelector = false;
    },
    formatDate(timestamp) {
      return Helper.formatDate(timestamp);
    },
    getCaseIcon(type) {
      return Helper.socialMediaIcons(type);
    },
    closeModal(response) {
      if (!this.isProcessing) {
        this.$emit("close", response);
      }
    },
    saveChanges() {
      this.isProcessing = true;
      this.districtError = false;
      this.dateTimeError = false;

      if (this.models.lat == "" || this.models.lat == "0") {
        this.models.lat = null;
      }

      if (this.models.lng == "" || this.models.lng == "0") {
        this.models.lng = null;
      }

      // if (!parseFloat(this.models.section) && this.models.section != 0) {
      //   this.isProcessing = false;

      //   return Swal.fire("Section is required", "", "error");
      // }

      if (!this.models.timestamp) {
        this.isProcessing = false;
        this.dateTimeError = true;

        return Swal.fire("Please select a date & time", "", "error");
      }

      // if (!this.models.reportType) {
      //   this.isProcessing = false;

      //   return Swal.fire("Please select work scope", "", "error");
      // }

      // if (!this.models.district || !this.models.road) {
      if (!this.models.district) {
        this.isProcessing = false;
        this.districtError = true;

        return Swal.fire("Please select a district", "", "error");
      }

      // Derived fields (backend recomputes these too — kept in sync here so
      // the response object shown after save matches what was stored).
      if (this.isR02) {
        this.models.width = null;
        this.models.mix_type = null;
        this.models.premix_quantity = null;
      } else {
        this.models.premix_quantity = this.derivedPremix || null;
      }

      if (this.show !== "add") {
        API.put(`cases/${this.currentCase._id}`, this.models)
          .then((retVal) => {
            this.isProcessing = false;

            this.closeModal(retVal.data);
          })
          .catch((err) => {
            this.isProcessing = false;

            console.error(err);
          });
      } else {
        let body = JSON.parse(JSON.stringify(this.models));

        if (!body.road) delete body.road;

        body.source = "agent";
        body.type = "mobile";
        body.status = "received";

        API.post("cases", body)
          .then((retVal) => {
            this.isProcessing = false;

            this.closeModal(retVal.data);
          })
          .catch((err) => {
            this.isProcessing = false;

            console.error(err);
          });
      }
    },
  },
};
</script>